'use client';

import { useMemo } from 'react';
import CustomSelect from '@/components/ui/CustomSelect';
import { Input } from '@/components/ui/Input';
import { DateField } from '@/components/ui/DateField';
import { UKRAINE_REGIONS } from '@/lib/constants/locations';
import { getSortedCountryOptions } from '@/lib/utils/sortLocalized';

// Reuse translation function type from LanguageContext
type TFunction = (key: string) => string;

// --- CountryField ---

interface CountryFieldProps {
  value: string;
  onChange: (value: string) => void;
  language: string;
  t: TFunction;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

export function CountryField({ value, onChange, language, t, error, disabled, required }: CountryFieldProps) {
  const options = useMemo(() => {
    const sorted = getSortedCountryOptions(language);
    return [
      { value: '', label: t('profile.selectCountry') },
      ...sorted,
    ];
  }, [language, t]);

  return (
    <CustomSelect
      label={t('profile.country')}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={t('profile.selectCountry')}
      searchable
      searchPlaceholder={language === 'uk' ? 'Пошук країни...' : 'Search country...'}
      error={error}
      disabled={disabled}
      required={required}
    />
  );
}

// --- RegionField ---

interface RegionFieldProps {
  value: string;
  onChange: (value: string) => void;
  countryValue: string;
  language: string;
  t: TFunction;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

export function RegionField({ value, onChange, countryValue, language, t, error, disabled, required }: RegionFieldProps) {
  const isUkraine = countryValue === 'UA';

  if (isUkraine) {
    const regionOptions = [
      { value: '', label: t('profile.selectRegion') },
      ...UKRAINE_REGIONS.map((r) => ({
        value: r.value,
        label: r.label,
      })),
    ];

    return (
      <CustomSelect
        label={t('profile.region')}
        value={value}
        onChange={onChange}
        options={regionOptions}
        placeholder={t('profile.selectRegion')}
        error={error}
        disabled={disabled}
        required={required}
      />
    );
  }

  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={countryValue ? (language === 'uk' ? 'Введіть регіон' : 'Enter region') : (language === 'uk' ? 'Спочатку виберіть країну' : 'Select country first')}
      label={t('profile.region')}
      error={error}
      disabled={disabled || !countryValue}
      required={required}
    />
  );
}

// --- EducationField ---

interface EducationFieldProps {
  value: string;
  onChange: (value: string) => void;
  otherValue: string;
  onOtherChange: (value: string) => void;
  language: string;
  t: TFunction;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

export function EducationField({ value, onChange, otherValue, onOtherChange, language, t, error, disabled, required }: EducationFieldProps) {
  const options = useMemo(() => [
    { value: '', label: language === 'uk' ? 'Виберіть освіту' : 'Select education' },
    { value: 'Вища медична', label: language === 'uk' ? t('profile.educationOptions.higherMedical') : `${t('profile.educationOptions.higherMedical')} (Вища медична)` },
    { value: 'Вища немедична', label: language === 'uk' ? t('profile.educationOptions.higherNonMedical') : `${t('profile.educationOptions.higherNonMedical')} (Вища немедична)` },
    { value: 'Середня медична', label: language === 'uk' ? t('profile.educationOptions.middleMedical') : `${t('profile.educationOptions.middleMedical')} (Середня медична)` },
    { value: 'Професійно-технічна', label: language === 'uk' ? t('profile.educationOptions.professionalTechnical') : `${t('profile.educationOptions.professionalTechnical')} (Професійно-технічна)` },
    { value: 'Інша', label: language === 'uk' ? t('profile.educationOptions.other') : `${t('profile.educationOptions.other')} (Інша)` },
  ], [language, t]);

  return (
    <div className="space-y-2">
      <CustomSelect
        label={t('profile.education')}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={language === 'uk' ? 'Виберіть освіту' : 'Select education'}
        error={error}
        disabled={disabled}
        required={required}
      />
      {value === 'Інша' && (
        <Input label={t('profile.specifyEducation')} value={otherValue} maxLength={120} onChange={e => onOtherChange(e.target.value)} placeholder={t('profile.specifyEducationPlaceholder')} />
      )}
    </div>
  );
}

// --- DateOfBirthField ---

interface DateOfBirthFieldProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  label: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
}

export function DateOfBirthField({ value, onChange, label, error, disabled, required, placeholder }: DateOfBirthFieldProps) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2 break-words">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <DateField
        value={value}
        onChange={onChange}
        placeholder={placeholder || 'дд.мм.рррр'}
        maxDate={new Date()}
        allowClear
        disabled={disabled}
      />
      {error && <p className="mt-1 text-xs sm:text-sm text-red-600 break-words">{error}</p>}
    </div>
  );
}
